import React, { useMemo, useState } from 'react';
import { Modal, Form, Input, Checkbox, Button, Typography, message } from 'antd';
import { MailOutlined, CheckCircleOutlined } from '@ant-design/icons';

const { Title, Paragraph } = Typography;

export interface TrialRequestModalProps {
  open: boolean;
  onClose: () => void;
}

const FREE_EMAIL_PROVIDERS = [
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'live.com',
  'aol.com', 'icloud.com', 'mail.com', 'protonmail.com', 'yandex.com',
  'gmx.com', 'zoho.com', 'msn.com', 'inbox.com', 'mail.ru',
];

const validateCompanyEmail = (_: unknown, value: string) => {
  if (!value) {
    return Promise.reject(new Error('Please enter your email'));
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
    return Promise.reject(new Error('Please enter a valid email'));
  }
  const domain = value.toLowerCase().split('@')[1];
  if (FREE_EMAIL_PROVIDERS.includes(domain)) {
    return Promise.reject(new Error('Please use your company email address'));
  }
  return Promise.resolve();
};

const generateSessionId = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

const TrialRequestModal: React.FC<TrialRequestModalProps> = ({ open, onClose }) => {
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [pageLoadTime] = useState(() => Date.now());

  const sessionId = useMemo(() => {
    const existing = typeof window !== 'undefined' ? localStorage.getItem('waitlist_session_id') : null;
    if (existing) return existing;
    const id = generateSessionId();
    if (typeof window !== 'undefined') {
      localStorage.setItem('waitlist_session_id', id);
    }
    return id;
  }, []);

  const handleCancel = () => {
    onClose();
    setTimeout(() => {
      setIsSubmitted(false);
      form.resetFields();
    }, 200);
  };

  const handleSubmit = async (values: { email: string; marketing_opt_in: boolean }) => {
    if (isSubmitting) return;

    message.success("You're on the list. We'll reach out when your access is approved.");
    setIsSubmitted(true);
    form.resetFields();
    setIsSubmitting(true);

    try {
      const hiddenFields = {
        referrer_url: document.referrer || undefined,
        landing_path: window.location.pathname,
        session_id: sessionId,
        honeypot: '',
        submit_timing: Date.now() - pageLoadTime,
      };

      const response = await fetch('https://pharmadashv2-backend.onrender.com/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: values.email,
          marketing_opt_in: values.marketing_opt_in,
          ...hiddenFields,
        }),
      });

      if (!response.ok) {
        throw new Error('Submission failed');
      }

      const result = await response.json();
      if (result.status !== 'accepted' && result.status !== 'ok') {
        throw new Error(result.message || 'Submission failed');
      }
    } catch (error) {
      console.error('Waitlist submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      title="Request Free Trial"
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={500}
      destroyOnClose
    >
      {!isSubmitted ? (
        <Form form={form} layout="vertical" onFinish={handleSubmit} autoComplete="off">
          <Form.Item
            name="email"
            label="Company Email"
            rules={[{ validator: validateCompanyEmail }]}
          >
            <Input
              placeholder="your.email@company.com"
              size="large"
              prefix={<MailOutlined />}
            />
          </Form.Item>

          <Form.Item
            name="marketing_opt_in"
            valuePropName="checked"
            rules={[{
              validator: (_, value) =>
                value ? Promise.resolve() : Promise.reject(new Error('Please accept to continue')),
            }]}
          >
            <Checkbox>
              I agree to receive updates about the trial and marketing communications.
            </Checkbox>
          </Form.Item>

          <Form.Item>
            <Button type="primary" size="large" loading={isSubmitting} block htmlType="submit">
              Request Free Trial
            </Button>
          </Form.Item>
        </Form>
      ) : (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <CheckCircleOutlined style={{ fontSize: '48px', color: '#52c41a', marginBottom: '16px' }} />
          <Title level={4}>You're on the list!</Title>
          <Paragraph>We'll reach out when your access is approved.</Paragraph>
        </div>
      )}
    </Modal>
  );
};

export default TrialRequestModal;
